import React, { memo, useRef, useState } from "react";
import Notification from "../../util/Notification/Notification";
import { useIntl } from "react-intl";
import { BprocFormOutcome } from "cuba/entities/bproc/bproc_FormOutcome";
import Button, { ButtonType } from "../../components/Button/Button";
import { AdditionalApproverModal } from "./ui/additional-approver-modal";
import { FormComponentProps } from "antd/lib/form";
import { restServices } from "cuba/services";
import { useApplicationStore } from "app/entities/form-controller/abstract";
import { goBackOrHomePage } from "../../util";
import { useHistory } from "react-router-dom";
import { CustomSelectedActors } from "app/pages/Bproc/modal/StartBprocModal";

interface OutcomeButtonsProps extends FormComponentProps {
  outcome: BprocFormOutcome;
}

export const OutcomeButtons = memo((props: OutcomeButtonsProps) => {
  const { outcome, form } = props;
  const store = useApplicationStore();

  const updateAndCommit = store((s) => s.updateAndCommit);
  const loadApplication = store((s) => s.loadApplication);
  const validate = store((s) => s.validate);
  const validationRequiredOutcomes = store((s) => s.validationRequiredOutcomes);
  const commentRequiredOutcomes = store((s) => s.commentRequiredOutcomes);
  const bpmStore = store((s) => s.bpmStore((bpm) => bpm));
  const { dataInstance } = store();
  const [customSelectedActors, setCustomSelectedActors] = useState<
    CustomSelectedActors[]
  >([]);

  const [commentText, setCommentText] = useState<string>("");

  const needValidation = validationRequiredOutcomes.includes(outcome.id);
  const intl = useIntl();

  const idRef = useRef("bproc-comment-" + Math.random().toString(36).slice(2));
  const id: string = idRef.current;

  const modalStoreState = bpmStore.modalStore((m) => m);
  const { beforeCompletePredicate } = modalStoreState;

  const modalControl = {
    handleValidationError: () => {
      Notification.error({
        message: intl.formatMessage({
          id: "management.editor.validationError",
        }),
      });
    },

    validateAndThen: (callback: () => void) => {
      validate &&
        validate()
          .then((isValid) => {
            if (isValid) callback();
          })
          .catch(modalControl.handleValidationError);
    },

    setModalVisible: (outcome: BprocFormOutcome) => {
      const outcomeId = outcome.id;
      if (beforeCompletePredicate) {
        beforeCompletePredicate(outcomeId).then((shouldOpen) => {
          if (shouldOpen) modalStoreState.openModal(outcomeId, true);
        });
      } else {
        modalStoreState.openModal(outcomeId, true);
      }
    },

    showModal: (outcome: BprocFormOutcome) => {
      const outcomeId = outcome.id;

      if (outcomeId === "REJECT") {
        if (needValidation) {
          modalControl.validateAndThen(() =>
            modalControl.setModalVisible(outcome),
          );
        } else {
          modalControl.setModalVisible(outcome);
        }
        return;
      }

      if (!modalStoreState.modalVisibleMap[outcomeId]) {
        if (needValidation) {
          modalControl.validateAndThen(() =>
            modalControl.setModalVisible(outcome),
          );
        } else {
          modalControl.setModalVisible(outcome);
        }
      }
    },
  };

  const title = intl.formatMessage({
    id:
      outcome.id === "APPROVE" && modalStoreState.recruiterEnable
        ? "Appoint"
        : outcome.id,
  });
  const history = useHistory();
  const callback = () => {};
  const afterSendOnApprove = () => {
    goBackOrHomePage(history);
  };

  //TODO #REFACTORING
  const completeWithOutcome = async (outcome: BprocFormOutcome) => {
    if (customSelectedActors?.length) {
      const elem = customSelectedActors[0];
      const res = await restServices.bprocService.saveNextApprovers({
        selectedApprovers: elem.selectedApprovers,
        currentTaskCode: bpmStore.activeTask?.name,
        request: dataInstance,
      });
    }

    try {
      await restServices.bprocTaskService.completeWithOutcome({
        taskData: bpmStore.activeTask,
        outcomeId: outcome?.id,
        processVariables: {
          comment:
            form.getFieldValue(id) !== undefined
              ? form.getFieldValue(id)
              : commentText,
        },
      });

      if (callback && outcome.id === "SEND_FOR_APPROVAL") {
        await callback();
      }
      await loadApplication({ ...store.getState().initialValue, form });
      modalStoreState.closeModal(outcome.id);

      if (afterSendOnApprove) afterSendOnApprove();

      Notification.success({
        message: intl.formatMessage({ id: "bproc." + outcome.id + ".success" }),
      });
    } catch (error) {
      Notification.error({
        message: intl.formatMessage({ id: "management.editor.error" }),
      });
    } finally {
      modalStoreState.setIsLoadingModal(false);
    }
  };

  const handleOk = async (outcome: BprocFormOutcome) => {
    try {
      const values = await new Promise<any>((resolve, reject) => {
        form.validateFields([id], { force: true }, (err, values) =>
          err ? reject(err) : resolve(values),
        );
      });

      modalStoreState.setIsLoadingModal(true);

      if (!updateAndCommit) {
        await completeWithOutcome(outcome);
        return;
      }

      const isActive = await restServices.bprocService.isActiveTask({
        taskData: bpmStore.activeTask,
      });

      if (!isActive) {
        Notification.info({
          message: intl.formatMessage({ id: "task.not.active" }),
        });
        return;
      }

      try {
        await updateAndCommit();
        await completeWithOutcome(outcome);

        await bpmStore.getProcessInstanceData(
          bpmStore.processInstanceBusinessKey,
          bpmStore.processDefinitionKey,
        );
      } catch (e) {
        Notification.error({
          message: intl.formatMessage({ id: "management.editor.error" }),
        });
      }
    } catch (err) {
      Notification.error({
        message: intl.formatMessage({
          id: "management.editor.validationError",
        }),
      });
    }
  };

  const handleCancel = (outcome: BprocFormOutcome) => {
    modalStoreState.closeModal(outcome.id);
    form.resetFields([id]);
    setCommentText("");
  };

  const commentValidator = (rule: any, value: any, callback: any) => {
    if (!value && commentRequiredOutcomes?.find((o) => o === outcome?.id)) {
      callback(intl.formatMessage({ id: "comment.required" }));
      return;
    }
    callback();
  };

  return (
    <Button
      buttonType={ButtonType.PRIMARY}
      className={outcome.id?.toLowerCase()}
      onClickCapture={() => modalControl.showModal(outcome)}
      key={outcome.id}
    >
      {title}
      {modalStoreState.modalVisibleMap.get(outcome.id) && (
        <AdditionalApproverModal
          outcome={outcome}
          title={title}
          loading={modalStoreState.isLoadingModal}
          modalVisibleMap={modalStoreState.modalVisibleMap}
          handleOk={handleOk}
          handleCancel={handleCancel.bind(null, outcome)}
          form={form}
          idText={id}
          customSelectedActors={customSelectedActors}
          setCustomSelectedActors={setCustomSelectedActors}
          commentRequiredOutcomes={commentRequiredOutcomes}
          openedOutcomeModal={outcome?.id}
          commentValidator={commentValidator}
          updateCommentText={setCommentText}
        />
      )}
    </Button>
  );
});

OutcomeButtons.displayName = "OutcomeButtons";
